import axios from './RequestProvider';
import { BASE_URL } from 'config';

export interface APIResponse {
    status: 'success' | 'error';
    message: string;
    data: any;
}

// All HTTP helpers return a guaranteed { status, message, data } shape.
// Chakra's toast requires status to be one of success|error|info|warning|loading,
// so a missing/invalid status was crashing the UI ("Cannot read properties of
// undefined (reading 'colorScheme')").

function normalizeResponse(res: any): APIResponse {
    const data = res?.data ?? res;
    if (data && typeof data === 'object' && (data.status === 'success' || data.status === 'error')) {
        return {
            status: data.status,
            message: typeof data.message === 'string' ? data.message : '',
            data: data.data ?? null,
        };
    }
    return { status: 'success', message: '', data };
}

function normalizeError(error: any): APIResponse {
    if (error?.response?.data && typeof error.response.data === 'object') {
        const d = error.response.data;
        return {
            status: 'error',
            message: typeof d.message === 'string' && d.message ? d.message : 'Request failed',
            data: d.data ?? null,
        };
    }
    const status = error?.response?.status;
    const msg =
        status === 401 ? 'Unauthorized'
            : status === 422 ? 'Validation failed'
            : status === 404 ? 'Not found'
            : error?.message || 'Network error';
    return { status: 'error', message: msg, data: null };
}

const validateStatus = (status: number) => status >= 200;

export const POST = async (url: string, data: any = {}, _headers = {}): Promise<APIResponse> => {
    try {
        const res = await axios.post(url, data, { validateStatus });
        return normalizeResponse(res);
    } catch (error) {
        return normalizeError(error);
    }
};

export const PUT = async (url: string, data: any = {}, _headers = {}): Promise<APIResponse> => {
    try {
        const res = await axios.put(url, data, { validateStatus });
        return normalizeResponse(res);
    } catch (error) {
        return normalizeError(error);
    }
};

export const GET = async (url: string, _headers = {}): Promise<APIResponse> => {
    try {
        const res = await axios.get(url, { validateStatus });
        return normalizeResponse(res);
    } catch (error) {
        return normalizeError(error);
    }
};

export const GETDownloadFile = async (url: string, _headers = {}): Promise<any> => {
    try {
        const res = await axios.get(url, {
            headers: {
                'content-disposition': 'attachment; filename*=UTF-8',
                'content-type':
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
            validateStatus,
            responseType: 'blob',
        });
        return res.data;
    } catch (error: any) {
        return normalizeError(error);
    }
};

export const DELETE = async (url: string, data: any = {}, _headers = {}): Promise<APIResponse> => {
    try {
        const res = await axios.delete(url, { data, validateStatus });
        return normalizeResponse(res);
    } catch (error) {
        return normalizeError(error);
    }
};

export const GETFile = async (url: string): Promise<File> => {
    const target = BASE_URL.replace(/\/$/, '') + '/api/Documents/GetImage?url=' + encodeURIComponent(url);
    const response = await axios.get(target, {
        withCredentials: true,
        responseType: 'blob',
    });
    const fileName = url.split('/').pop() ?? 'file';
    return new File([response.data], fileName, { type: response.data.type });
};
